import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Rappel } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface RappelState {
  rappels: Rappel[];
  rappel: Rappel | null;
  loading: boolean;
  error: string | null;
}

const initialState: RappelState = {
  rappels: [],
  rappel: null,
  loading: false,
  error: null,
};


export const fetchRappels = createAsyncThunk<Rappel[], void, { state: RootState }>(
  'rappel/fetchRappels',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/rappels/all/${authentification.user.OwnerId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rappels');
    }
  }
);

export const fetchRappel = createAsyncThunk<Rappel, number, { state: RootState }>(
  'rappel/fetchRappel',
  async (rappelId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/rappels/${rappelId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchRappels());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rappel');
    }
  }
);

export const createRappel = createAsyncThunk<Rappel, Partial<Rappel>, { state: RootState }>(
  'rappel/createRappel',
  async (rappelData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/rappels/`, rappelData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchRappels());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create rappel');
    }
  }
);

export const updateRappel = createAsyncThunk<
  Rappel,
  { rappelData: Partial<Rappel>; rappelId: number },
  { state: RootState }
>(
  'rappel/updateRappel',
  async ({ rappelData, rappelId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(`${baseUrlApi}/rappels/${rappelId}`, rappelData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchRappels());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update rappel');
    }
  }
);

export const deleteRappel = createAsyncThunk<number, number, { state: RootState }>(
  'rappel/deleteRappel',
  async (rappelId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      await axios.delete(`${baseUrlApi}/rappels/${rappelId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchRappels());
      return rappelId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete rappel');
    }
  }
);

const rappelSlice = createSlice({
  name: 'rappel',
  initialState,
  reducers: {
    clearRappel: (state) => { state.rappel = null; },
    clearRappels: (state) => { state.rappels = []; },
    resetRappelState: () => initialState,
  },
  extraReducers: (builder) => {
    [fetchRappels, fetchRappel, createRappel, updateRappel, deleteRappel].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchRappels.fulfilled, (state, action) => {
        state.loading = false;
        state.rappels = action.payload;
      })
      .addCase(fetchRappel.fulfilled, (state, action) => {
        state.loading = false;
        state.rappel = action.payload;
      })
      .addCase(createRappel.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateRappel.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteRappel.fulfilled, (state, action) => {
        state.loading = false;
        state.rappels = state.rappels.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearRappel, clearRappels, resetRappelState } = rappelSlice.actions;
export default rappelSlice.reducer;
